/**
 * Descargar datos: the shop's records as spreadsheets.
 *
 * For the accountant at the end of the month, for the owner who wants a year of sales in Excel,
 * and for the day somebody wants to take their records elsewhere: whatever the app knows, in a
 * file anybody can open. CSV, because every spreadsheet reads it, a phone can share it and it
 * needs no library — with a byte-order mark, which is what makes Excel read the accents right.
 *
 * Tables are built from this device's copy, in the language the screen is in. Money is córdobas
 * with a decimal point, the way Nicaragua writes it, and dates stay `YYYY-MM-DD`, which a
 * spreadsheet reads as a date whatever its language.
 */

import {
  centavos,
  costOf,
  costoDeUnidadVenta,
  fromUnits,
  fxRate,
  usdToCordobas,
  type Centavos,
  type CuentaBancoConfig,
} from '@abastece/shared';
import {
  accountByCode,
  esDeudaAnterior,
  netoDeVenta,
  pendienteDeCompra,
  type Abono,
  type Cliente,
  type Compra,
  type Existencia,
  type Gasto,
  type JournalEntry,
  type ListaPrecio,
  type Precio,
  type Producto,
  type UnidadVenta,
  type Venta,
} from '@abastece/core';
import type { Clave } from '../i18n/index.js';
import {
  etiquetaCategoriaGasto,
  etiquetaCategoriaProducto,
  etiquetaCuenta,
  etiquetaLugar,
  etiquetaUnidad,
} from '../i18n/dominio.js';
import { etiquetaListaPrecio, ordenarListas, precioDe, unidadesDe } from './catalogo.js';

export type Celda = string | number | null | undefined;

export interface Tabla {
  columnas: string[];
  filas: Celda[][];
}

type T = (clave: Clave, valores?: Record<string, string | number>) => string;

export interface Rango {
  desde?: string;
  hasta?: string;
}

const dentro = (fecha: string, r: Rango) => (!r.desde || fecha >= r.desde) && (!r.hasta || fecha <= r.hasta);

// ---------------------------------------------------------------------------
// CSV
// ---------------------------------------------------------------------------

const BOM = '﻿';

/**
 * A spreadsheet runs a cell that starts with one of these as a formula. A product named
 * "=HYPERLINK(…)" is text, and must open as text on the accountant's computer.
 */
const FORMULA = /^[=+\-@\t\r]/;

export function celdaCsv(c: Celda): string {
  if (c === null || c === undefined) return '';
  if (typeof c === 'number') return Number.isFinite(c) ? String(c) : '';
  const texto = FORMULA.test(c) ? `'${c}` : c;
  return /[",\r\n]/.test(texto) ? `"${texto.replace(/"/g, '""')}"` : texto;
}

export function aCsv(tabla: Tabla): string {
  return BOM + [tabla.columnas, ...tabla.filas].map((f) => f.map(celdaCsv).join(',')).join('\r\n') + '\r\n';
}

/** Centavos as córdobas: a number a spreadsheet can add up. */
const cs = (c: number) => c / 100;
/** Milli as units. */
const ud = (m: number) => m / 1000;
const siNo = (t: T, v: boolean) => t(v ? 'comun.si' : 'comun.no');

// ---------------------------------------------------------------------------
// Las tablas
// ---------------------------------------------------------------------------

const FORMAS_VENTA = ['efectivo_cordobas', 'efectivo_dolares', 'transferencia', 'billetera', 'tarjeta', 'credito'] as const;

const etiquetaFormaVenta = (t: T, forma: string) =>
  forma === 'credito' ? t('pago.fiado') : t(`pago.${forma}` as Clave);

const etiquetaFormaPago = (t: T, forma: string) =>
  forma === 'efectivo_dolares'
    ? t('gastos.efectivoUsd')
    : forma === 'credito'
      ? t('pago.credito')
      : t(`pago.${forma}` as Clave);

/** How a purchase was paid: one way, or each of its parts and what paid the rest (`conLaCompra`). */
function formaDeCompra(t: T, c: Compra): string {
  // A voided purchase's parts as they stood when it was voided: not one voided on its own before.
  const partes = (c.pagos ?? []).filter((x) => x.conLaCompra && (!x.anulado || (c.anulada && x.anuladoConLaCompra)));
  if (partes.length === 0) return etiquetaFormaPago(t, c.formaPago);
  const resto = partes.reduce((a, x) => a - x.monto, c.totalCordobas as number);
  return [
    ...partes.map((x) => `${etiquetaFormaPago(t, x.formaPago)} ${cs(x.monto).toFixed(2)}`),
    ...(resto > 0 ? [`${etiquetaFormaPago(t, c.formaPago)} ${cs(resto).toFixed(2)}`] : []),
  ].join(' + ');
}

/** How an expense was paid: one way, or each of its parts in córdobas and what paid the rest (`conElGasto`), as a purchase's. */
function formaDeGasto(t: T, g: Gasto): string {
  const partes = (g.pagos ?? []).filter((x) => x.conElGasto && (!x.anulado || (g.anulado && x.anuladoConElGasto)));
  if (partes.length === 0) return etiquetaFormaPago(t, g.formaPago);
  const resto = partes.reduce((a, x) => a - x.monto, g.montoCordobas as number);
  return [
    ...partes.map((x) => `${etiquetaFormaPago(t, x.formaPago)} ${cs(x.monto).toFixed(2)}`),
    `${etiquetaFormaPago(t, g.formaPago)} ${cs(resto).toFixed(2)}`,
  ].join(' + ');
}

/** One row a sale: what it came to and how it was paid. */
export function tablaVentas(ventas: readonly Venta[], clientes: ReadonlyMap<string, string>, rango: Rango, t: T): Tabla {
  return {
    columnas: [
      t('exportar.col.fecha'),
      t('exportar.col.ticket'),
      t('exportar.col.cliente'),
      t('exportar.col.total'),
      t('exportar.col.descuento'),
      t('exportar.col.envio'),
      ...FORMAS_VENTA.map((f) => etiquetaFormaVenta(t, f)),
      t('cobro.vuelto'),
      t('exportar.col.devuelto'),
      t('exportar.col.costo'),
      t('exportar.col.utilidad'),
      t('exportar.col.anulada'),
    ],
    filas: ordenarPorFecha(ventas.filter((v) => dentro(v.fecha, rango)), (v) => v.ticket).map((v) => {
      // As «Contabilidad» counts a sale (`netoDeVenta`): what came back off both, the delivery and
      // the cash rounding not part of what it made, and a voided one nothing. It used to be the
      // total less the cost, returns and all (2026-10-13).
      const neto = v.anulada ? { ingreso: 0, costo: 0 } : netoDeVenta(v);
      return [
        v.fecha,
        v.ticket,
        v.clienteId ? (clientes.get(v.clienteId) ?? '') : '',
        cs(v.total),
        cs(v.descuento),
        cs(v.envio ?? 0),
        ...FORMAS_VENTA.map((f) => cs(v.pagos.filter((p) => p.forma === f).reduce((a, p) => a + p.monto, 0))),
        cs(v.cambio),
        cs(v.devuelto ?? 0),
        cs(neto.costo),
        cs(neto.ingreso - neto.costo),
        siNo(t, v.anulada),
      ];
    }),
  };
}

/** One row a line sold: for adding up by product. */
export function tablaVentasDetalle(
  ventas: readonly Venta[],
  clientes: ReadonlyMap<string, string>,
  rango: Rango,
  t: T,
): Tabla {
  return {
    columnas: [
      t('exportar.col.fecha'),
      t('exportar.col.ticket'),
      t('exportar.col.cliente'),
      t('exportar.col.producto'),
      t('exportar.col.cantidad'),
      t('exportar.col.devuelto'),
      t('exportar.col.precio'),
      t('exportar.col.subtotal'),
      t('exportar.col.costo'),
      t('exportar.col.anulada'),
    ],
    filas: ordenarPorFecha(ventas.filter((v) => dentro(v.fecha, rango)), (v) => v.ticket).flatMap((v) =>
      v.lineas.map((l) => [
        v.fecha,
        v.ticket,
        v.clienteId ? (clientes.get(v.clienteId) ?? '') : '',
        // As it was sold: the receipt's name, and a quick sale's typed description.
        l.descripcion,
        ud(l.cantidad),
        ud(l.devuelta ?? 0),
        cs(l.precioUnitario),
        cs(l.subtotal),
        cs(l.costoPosteado),
        siNo(t, v.anulada),
      ]),
    ),
  };
}

/** What is on hand, where, and what it is worth at average cost. Today, not a period. */
export function tablaInventario(productos: readonly Producto[], existencias: readonly Existencia[], t: T): Tabla {
  const porId = new Map(productos.map((p) => [p.id, p]));
  const filas = existencias
    .map((e) => ({ e, p: porId.get(e.productoId) }))
    .filter((x): x is { e: Existencia; p: Producto } => x.p !== undefined)
    .sort((a, b) => a.p.nombre.localeCompare(b.p.nombre, 'es') || a.e.ubicacion.localeCompare(b.e.ubicacion));
  return {
    columnas: [
      t('exportar.col.producto'),
      t('exportar.col.categoria'),
      t('exportar.col.lugar'),
      t('exportar.col.cantidad'),
      t('exportar.col.unidad'),
      t('exportar.col.costoUnitario'),
      t('exportar.col.valor'),
      t('exportar.col.minimo'),
      t('exportar.col.activo'),
    ],
    filas: filas.map(({ e, p }) => [
      p.nombre,
      etiquetaCategoriaProducto(p.categoria),
      etiquetaLugar(e.ubicacion),
      ud(e.cantidad),
      etiquetaUnidad(p.unidadBase, p.unidadBase),
      cs(costoDeUnidadVenta(e.costoPromedio, fromUnits(1))),
      // Below zero too, as the books carry it (`reportes/cuadres.ts`): at nothing, the sheet added up
      // to more than «Inventario» says (2026-10-20).
      cs(costOf(e.costoPromedio, e.cantidad)),
      p.stockMinimo != null ? ud(p.stockMinimo) : null,
      siNo(t, p.activo),
    ]),
  };
}

/** Every presentation of every product, with its price on each list: the price book. */
export function tablaPrecios(
  productos: readonly Producto[],
  unidades: readonly UnidadVenta[],
  precios: readonly Precio[],
  listas: readonly ListaPrecio[],
  t: T,
): Tabla {
  const activas = ordenarListas(listas);
  return {
    columnas: [
      t('exportar.col.producto'),
      t('exportar.col.categoria'),
      t('exportar.col.presentacion'),
      t('exportar.col.contiene'),
      t('exportar.col.codigos'),
      ...activas.map((l) => etiquetaListaPrecio(l)),
    ],
    filas: [...productos]
      .filter((p) => p.activo)
      .sort((a, b) => a.nombre.localeCompare(b.nombre, 'es'))
      .flatMap((p) =>
        unidadesDe(unidades, p.id).map((u) => [
          p.nombre,
          etiquetaCategoriaProducto(p.categoria),
          u.nombre,
          `${ud(u.factor)} ${etiquetaUnidad(p.unidadBase, p.unidadBase)}`,
          p.codigosBarras.join(' '),
          ...activas.map((l) => {
            const precio = precioDe(precios, u.id, l.id);
            return precio === null ? null : cs(precio);
          }),
        ]),
      ),
  };
}

/** The clients, and what each owes. Today, not a period. */
export function tablaClientes(clientes: readonly Cliente[], listas: readonly ListaPrecio[], t: T): Tabla {
  const lista = new Map(listas.map((l) => [l.id, etiquetaListaPrecio(l)]));
  return {
    columnas: [
      t('exportar.col.cliente'),
      t('exportar.col.tipo'),
      t('exportar.col.telefono'),
      t('exportar.col.direccion'),
      t('exportar.col.cedula'),
      t('exportar.col.lista'),
      t('exportar.col.limite'),
      t('exportar.col.saldo'),
      t('exportar.col.activo'),
    ],
    filas: [...clientes]
      .sort((a, b) => a.nombre.localeCompare(b.nombre, 'es'))
      .map((c) => [
        c.nombre,
        t(c.tipo === 'mayorista' ? 'clientes.mayorista' : 'clientes.mostrador'),
        c.telefono,
        c.direccion,
        c.cedula ?? null,
        c.listaId ? (lista.get(c.listaId) ?? '') : '',
        c.limiteCredito != null ? cs(c.limiteCredito) : null,
        cs(c.saldo ?? 0),
        siNo(t, c.activo),
      ]),
  };
}

/** What clients paid off their fiado. */
export function tablaAbonos(abonos: readonly Abono[], clientes: ReadonlyMap<string, string>, rango: Rango, t: T): Tabla {
  return {
    columnas: [
      t('exportar.col.fecha'),
      t('exportar.col.cliente'),
      t('exportar.col.monto'),
      t('exportar.col.forma'),
      t('exportar.col.referencia'),
      t('exportar.col.nota'),
      t('exportar.col.anulado'),
    ],
    filas: ordenarPorFecha(abonos.filter((a) => dentro(a.fecha, rango))).map((a) => [
      a.fecha,
      clientes.get(a.clienteId) ?? '',
      cs(a.monto),
      etiquetaFormaPago(t, a.formaPago),
      a.referencia,
      a.nota,
      siNo(t, a.anulado),
    ]),
  };
}

export function tablaGastos(gastos: readonly Gasto[], proveedores: ReadonlyMap<string, string>, rango: Rango, t: T): Tabla {
  return {
    columnas: [
      t('exportar.col.fecha'),
      t('exportar.col.categoria'),
      t('exportar.col.descripcion'),
      t('exportar.col.moneda'),
      t('exportar.col.montoOriginal'),
      t('exportar.col.monto'),
      t('exportar.col.forma'),
      t('exportar.col.proveedor'),
      t('exportar.col.referencia'),
      t('exportar.col.anulado'),
    ],
    filas: ordenarPorFecha(gastos.filter((g) => dentro(g.fecha, rango))).map((g) => [
      g.fecha,
      etiquetaCategoriaGasto(g.categoria),
      g.descripcion,
      g.moneda,
      cs(g.monto),
      cs(g.montoCordobas),
      // A shortage explained was paid by nobody: a count had already found the money gone.
      g.faltanteDe ? t('gastos.formaFaltante') : formaDeGasto(t, g),
      g.proveedorId ? (proveedores.get(g.proveedorId) ?? '') : '',
      g.referencia,
      siNo(t, g.anulado),
    ]),
  };
}

/** A purchase's IVA in córdobas: at the purchase's own rate, when it was in dollars. */
function ivaEnCordobas(c: Compra): Centavos {
  if (!c.iva) return centavos(0);
  return c.moneda === 'USD' && c.tasaCambio ? usdToCordobas(c.iva, fxRate(c.tasaCambio)) : c.iva;
}

export function tablaCompras(compras: readonly Compra[], proveedores: ReadonlyMap<string, string>, rango: Rango, t: T): Tabla {
  return {
    columnas: [
      t('exportar.col.fecha'),
      t('exportar.col.proveedor'),
      t('exportar.col.documento'),
      t('exportar.col.lugar'),
      t('exportar.col.forma'),
      t('exportar.col.vence'),
      // What the invoice charged in IVA, inside the total: part of what the goods cost.
      t('exportar.col.iva'),
      t('exportar.col.total'),
      t('exportar.col.pagado'),
      t('exportar.col.devuelto'),
      t('exportar.col.pendiente'),
      t('exportar.col.anulada'),
    ],
    // A debt from before is not a purchase of the period: the journal has it, against Capital.
    filas: ordenarPorFecha(compras.filter((c) => !esDeudaAnterior(c) && dentro(c.fecha, rango))).map((c) => [
      c.fecha,
      proveedores.get(c.proveedorId) ?? '',
      c.documento,
      etiquetaLugar(c.ubicacion),
      formaDeCompra(t, c),
      c.vencePago,
      cs(ivaEnCordobas(c)),
      cs(c.totalCordobas),
      cs(c.pagado),
      cs(c.devuelto ?? 0),
      cs(pendienteDeCompra(c)),
      siNo(t, c.anulada),
    ]),
  };
}

/** The journal, one row a line: what an accountant asks for first. */
export function tablaDiario(
  asientos: readonly JournalEntry[],
  rango: Rango,
  t: T,
  /** The owner's names for the bank accounts (`cuentasBanco`). */
  bancos: readonly CuentaBancoConfig[] = [],
): Tabla {
  return {
    columnas: [
      t('exportar.col.fecha'),
      t('exportar.col.concepto'),
      t('exportar.col.codigo'),
      t('exportar.col.cuenta'),
      t('exportar.col.debe'),
      t('exportar.col.haber'),
      t('exportar.col.nota'),
    ],
    filas: ordenarPorFecha(asientos.filter((a) => dentro(a.fecha, rango))).flatMap((a) =>
      a.lineas.map((l) => {
        const cuenta = accountByCode(l.cuenta);
        return [
          a.fecha,
          a.concepto,
          l.cuenta,
          cuenta ? etiquetaCuenta(l.cuenta, cuenta.name, bancos) : '',
          l.debe > 0 ? cs(l.debe) : null,
          l.haber > 0 ? cs(l.haber) : null,
          l.memo,
        ];
      }),
    ),
  };
}

/** Oldest first, and stable within a day: the order things happened in. */
function ordenarPorFecha<X extends { fecha: string }>(xs: readonly X[], desempate?: (x: X) => string): X[] {
  return [...xs].sort(
    (a, b) => a.fecha.localeCompare(b.fecha) || (desempate ? desempate(a).localeCompare(desempate(b)) : 0),
  );
}
